/**
 * Navigator
 *
 * Centralized navigation utility for the application.
 * Provides type-safe navigation methods to different screens.
 *
 * Usage:
 * 1. Import Navigator and initialize with react-router's navigate function
 * 2. Call navigation methods with proper parameters
 *
 * Benefits:
 * - Type-safe navigation
 * - Centralized route management
 * - Easy to refactor route paths
 * - Consistent navigation across the app
 *
 * Pattern matches Flutter's NavigationService
 *
 * Screen Key Strategy:
 * - All routes now use screen keys for consistent caching behavior
 * - Screen keys are automatically generated by RouteStack (no manual generation needed)
 * - Main navigation routes (/, /inbox, /more): persistCache: true → never removed from cache
 * - Detail routes: persistCache: false → removed from cache on POP navigation
 */

import type { NavigateFunction } from 'react-router-dom';
import { encodeMediaPathForRoute } from '@/presentation/utils/mediaPathUtils';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { store } from '@/presentation/store';
import { showBudgetExceededOverlay } from '@/presentation/store/slices/appOverlay/appOverlaySlice';
import type { CommentInfoEntity } from '@/domain/entities/comment/commentInfoEntity';
import type { ChatUsersInfoEntity } from '@/domain/entities/info/chatUsersInfoEntity';
import type { RoadmapItemInfoEntity } from '@/domain/entities/info/roadmapItemInfoEntity';
import type { RoadmapCommentInfoEntity } from '@/domain/entities/info/roadmapCommentInfoEntity';
import type { InAppFeedbackEntity } from '@/domain/entities/feedback/inAppFeedbackEntity';
import type { ChatMessageEntity } from '@/domain/entities/chat/chatMessageEntity';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export class Navigator {
    private static navigateFunction: NavigateFunction | null = null;

    /**
     * Initialize the Navigator with react-router's navigate function
     * Call this once in your app setup or use the hook useNavigator
     * 
     * @param navigate - The navigate function from useNavigate() hook
     */
    static setNavigate(navigate: NavigateFunction): void {
        Navigator.navigateFunction = navigate;
    }

    /**
     * Get the navigate function (internal use)
     */
    private static getNavigate(): NavigateFunction {
        if (!Navigator.navigateFunction) {
            throw new Error('Navigator not initialized. Call Navigator.setNavigate() first.');
        }
        return Navigator.navigateFunction;
    }

    /**
     * Navigate back (equivalent to browser back button)
     */
    static goBack(): void {
        Navigator.getNavigate()(-1);
    }

    /**
     * Deep-link-guarded pop. Pops in-app history when there is any, otherwise
     * runs `fallback` so a deep-link / refresh entry lands inside the app
     * instead of letting `navigate(-1)` walk off-site or no-op.
     *
     * This is the canonical page back-handler (`presentation.md` → App bar →
     * "Back affordance"); pass the page's own parent route, e.g.
     * `Navigator.goBackOr(() => Navigator.navigateToSupport())`.
     *
     * @param fallback - Where to land when there is no in-app history to pop.
     */
    static goBackOr(fallback: () => void): void {
        if (window.history.length > 1) Navigator.goBack();
        else fallback();
    }

    /**
     * Navigate forward
     */
    static goForward(): void {
        Navigator.getNavigate()(1);
    }

    /**
     * Navigate to user details page
     * Screen key is automatically generated by RouteStack
     * @param userId - Firebase user ID
     */
    static navigateToUserDetails(userId: string): void {
        Navigator.getNavigate()(`/${userId}`);
    }

    /**
     * Navigate to user details page by mention/username
     * Uses the same route as navigateToUserDetails but prefixes with @
     * to indicate this is a username lookup, not a Firebase ID (e.g., /@john)
     * Screen key is automatically generated by RouteStack
     * @param mention - Username (without @ symbol)
     */
    static navigateToUserDetailsByMention(mention: string): void {
        Navigator.getNavigate()(`/@${mention}`);
    }

    /**
     * Navigate to user videos page
     * Screen key is automatically generated by RouteStack
     * @param userId - Firebase user ID
     * @param options - Optional navigation options (medias and currentIndex for initial state)
     */
    static navigateToUserVideos(
        userId: string,
        options?: { medias?: any[]; currentIndex?: number }
    ): void {
        Navigator.getNavigate()(`/${userId}/videos`, { state: options });
    }

    /**
     * Navigate to content details page
     * Screen key is automatically generated by RouteStack
     * @param userId - Firebase user ID
     * @param userMediaId - User media ID
     * @param options - Optional navigation options (medias for initial state)
     */
    static navigateToContentDetails(
        userMediaId: string,
        options?: { medias?: any[] }
    ): void {
        Navigator.getNavigate()(`/content/${userMediaId}`, { state: options });
    }

    /**
     * Navigate to a content's details page by relative path. Mirrors Flutter's
     * `_onContentLinkTap` (base_content_widget.dart:283) which uses
     * `fetchSingleMediaPath`. Firestore stores `path` URL-encoded (e.g.
     * `media%2F<userId>%2F<mediaId>%2F`), and that exact form is what we must
     * query with. The URL trims the constant `media%2F` prefix and the
     * trailing `%2F` to keep the address bar tidy; `ContentDetailsPage`
     * reconstructs the full encoded value from the route param.
     *
     * `options.commentInfo` + `options.openCommentsSheet` mirror Flutter's
     * `_onCommentReplyTap` (base_content_widget.dart:296): the destination
     * page opens the comments sheet at the highlighted parent comment once
     * the media resolves.
     *
     * `options.openContentLikesSheet` + `options.openGiftsSheet` are the two
     * sibling deep-link flags Flutter's pager accepts
     * (content_list_pager_screen.dart:101-118): they open the "users who
     * liked" sheet and the "gifts received" sheet respectively, also once the
     * media resolves. The destination enforces Flutter's exact precedence —
     * comments > likes > gifts — so at most ONE sheet opens per navigation
     * even if several flags are set.
     */
    static navigateToContentDetailsByPath(
        relativePath: string,
        options?: {
            commentInfo?: CommentInfoEntity;
            openCommentsSheet?: boolean;
            openContentLikesSheet?: boolean;
            openGiftsSheet?: boolean;
        },
    ): void {
        Navigator.getNavigate()(
            `/content/@${encodeMediaPathForRoute(relativePath)}`,
            options ? { state: options } : undefined,
        );
    }

    /**
     * Navigate to the web-only full-screen default-bucket media viewer
     * (`DefaultMediaPage`) at `/default-media/:id`.
     *
     * The media is on the authenticated (but unencrypted) cloud default storage
     * bucket — not public — hence the `default-media` route name (not the old
     * `public-content`). Distinct from the chat-media viewer
     * (`navigateToMediaDetails` → `/chat/media/:id`), which renders client-E2EE
     * chat media.
     *
     * Web replacement for the destination of Flutter's `_onScreenshotTapped`
     * (`lib/presentation/screens/feedback/user_feedback_details_screen.dart:48-59`),
     * which pushes the content-list screen. On the web the feedback-details
     * screenshot tap navigates here instead.
     *
     * The `:id` path segment is a random id minted here purely so the route
     * cache treats every visit as a fresh instance — it is NOT read for data.
     * The real payload is the (nullable) `url`, passed via `location.state`.
     *
     * @param url - The media URL to display full-viewport, or null to show the
     *              centered "no media" error.
     * @param aspectRatio - The media's aspect ratio (width / height), used by the
     *              page to size the thumbnail box to the screenshot's true shape
     *              (height-capped at `85vh`). Omitted falls back to the page's
     *              default ratio.
     */
    static navigateToDefaultMedia(
        url: string | null,
        aspectRatio?: number,
    ): void {
        const randomId = Math.random().toString(36).substr(2, 9);
        Navigator.getNavigate()(`/default-media/${randomId}`, {
            state: { url, aspectRatio },
        });
    }

    /**
     * Navigate to the admin users-feedback details page at
     * `/admin/users-feedback/:id`.
     *
     * Web port of the destination of Flutter's feedback list row tap
     * (`UsersFeedbackDetailsScreen`,
     * `lib/presentation/screens/feedback/user_feedback_details_screen.dart`).
     * The whole `InAppFeedbackEntity` is passed via `location.state` so the
     * details page can render without a second Firestore round-trip — Flutter
     * also only ever constructs the screen from the full object, never by id.
     * `feedback.id` keeps the URL refresh-survivable, but there is no
     * fetch-by-id path: on deep link / refresh the page shows its not-found
     * message.
     *
     * @param feedback - The full feedback entity to display.
     */
    static navigateToUsersFeedbackDetails(feedback: InAppFeedbackEntity): void {
        Navigator.getNavigate()(`/admin/users-feedback/${feedback.id}`, {
            state: { feedback },
        });
    }

    /**
     * Navigate to the admin users-feedback list page at `/admin/users-feedback`.
     *
     * Web port of the destination of the admin sheet's "feedback" row, which in
     * Flutter pushes `UsersFeedbackScreen`
     * (`lib/presentation/screens/feedback/users_feedback_screen.dart`).
     * Screen key is automatically generated by RouteStack.
     */
    static navigateToUsersFeedback(): void {
        Navigator.getNavigate()('/admin/users-feedback');
    }

    /**
     * Navigate to settings screen
     * Screen key is automatically generated by RouteStack
     */
    static navigateToSettings(): void {
        Navigator.getNavigate()('/settings');
    }

    /**
     * Navigate to settings category screen
     * Screen key is automatically generated by RouteStack
     * @param category - UserSettingCategory enum value (number)
     */
    static navigateToSettingCategory(category: number): void {
        Navigator.getNavigate()(`/settings/${category}`);
    }

    /**
     * Navigate to premium packages page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToPremiumPackages(): void {
        Navigator.getNavigate()('/premium-packages');
    }

    /**
     * Navigate to the standalone Campaigns page (`/campaigns`).
     * Screen key is automatically generated by RouteStack.
     *
     * Mirrors Flutter's `NavigationService.navigateToCampaignsScreen()`
     * (lib/services/navigation_service.dart:2459-2480), which pushes the
     * standalone `CampaignsScreen` (`!fromHomeScreen` variant).
     */
    static navigateToCampaigns(): void {
        Navigator.getNavigate()('/campaigns');
    }

    /**
     * Navigate to the "add feedback" page (`/feedback`).
     * Screen key is automatically generated by RouteStack.
     *
     * Web port of Flutter's push of `InAppFeedbackScreen`
     * (`lib/presentation/screens/feedback/in_app_feedback_screen.dart:26-33`),
     * reached from the home top-bar menu's Feedback row. The page takes no
     * arguments — the draft description + screenshots live in its own hook state.
     */
    static navigateToFeedback(): void {
        Navigator.getNavigate()('/feedback');
    }

    /**
     * Navigate to the standalone admin Latest Users page (`/admin/latest-users`).
     * Screen key is automatically generated by RouteStack.
     *
     * Ports Flutter's navigation to `LatestUsersScreen`
     * (lib/presentation/screens/admin/admin_screen.dart `latestUsers` row →
     * `LatestUsersScreen`). On web this replaces the retired `LatestUsersSheet`.
     */
    static navigateToLatestUsers(): void {
        Navigator.getNavigate()('/admin/latest-users');
    }

    /**
     * Navigate to premium subscriptions policy page
     * Public route - no authentication required
     */
    static navigateToPremiumSubscriptionsPolicy(): void {
        Navigator.getNavigate()('/legal/premium-subscriptions-policy');
    }

    /**
     * Navigate to coins policy page
     * Public route - no authentication required
     */
    static navigateToCoinsPolicy(): void {
        Navigator.getNavigate()('/legal/coins-policy');
    }

    /**
     * Navigate to legal documents list page.
     * Public route - no authentication required.
     */
    static navigateToLegalDocuments(): void {
        Navigator.getNavigate()('/legal');
    }

    /**
     * Navigate to terms of use page.
     * Public route - no authentication required.
     */
    static navigateToTermsOfUse(): void {
        Navigator.getNavigate()('/legal/terms-of-use');
    }

    /**
     * Navigate to rewards policy page.
     * Public route - no authentication required.
     */
    static navigateToRewardsPolicy(): void {
        Navigator.getNavigate()('/legal/rewards-policy');
    }

    /**
     * Navigate to exclusive subscriptions policy page.
     * Public route - no authentication required.
     */
    static navigateToExclusiveSubscriptionsPolicy(): void {
        Navigator.getNavigate()('/legal/exclusive-subscriptions-policy');
    }

    /**
     * Navigate to privacy policy page.
     * Public route - no authentication required.
     */
    static navigateToPrivacyPolicy(): void {
        Navigator.getNavigate()('/legal/privacy-policy');
    }

    /**
     * Navigate to community guidelines page.
     * Public route - no authentication required.
     */
    static navigateToCommunityGuidelines(): void {
        Navigator.getNavigate()('/legal/community-guidelines');
    }

    /**
     * Navigate to the Contact page (support contact form).
     * Public route - no authentication required.
     */
    static navigateToContact(): void {
        Navigator.getNavigate()('/contact');
    }

    /**
     * Navigate to the Support list page.
     * Public route - no authentication required.
     */
    static navigateToSupport(): void {
        Navigator.getNavigate()('/support');
    }

    /**
     * Navigate to the About Expause page (static content catalogue).
     * Public route - no authentication required (reached from the
     * unauthenticated LoginPage "Learn more" affordance as well as the
     * authenticated Help & About sheet).
     */
    static navigateToAbout(): void {
        Navigator.getNavigate()('/about');
    }

    /**
     * Navigate to the public Delete Account page (Play-compliant
     * account-deletion flow).
     * Public route - no authentication required (reachable signed-in or not);
     * the page itself gates the delete control on a fresh authenticated
     * session.
     */
    static navigateToDeleteAccount(): void {
        Navigator.getNavigate()('/delete-account');
    }

    /**
     * Navigate to a specific Support item detail page by numeric id
     * (array index from `SupportUtils.items` — see `useSupportItems`).
     * Public route - no authentication required.
     */
    static navigateToSupportItem(id: number): void {
        Navigator.getNavigate()(`/support/${id}`);
    }

    /**
     * Navigate to the Mock Chat dev harness (`/mock-chat`).
     *
     * Dev-only surface — exercises `VirtualList(reversed)` with a cached
     * `string[]` and a composer. Not exposed in the TopBar; reach it via
     * URL bar or this helper from the console.
     */
    static navigateToMockChat(): void {
        Navigator.getNavigate()('/mock-chat');
    }

    /**
     * Navigate to the real chat (direct messaging) page at `/chat/:peerId`.
     *
     * The whole `ChatUsersInfoEntity` is passed via `location.state` so the
     * destination page can read it without a second Firestore round-trip on
     * in-app navigation. On deep-link / refresh, `ChatPage` rehydrates the
     * missing struct fields itself (see its JSDoc). The peer id in the URL
     * keeps the route refresh-survivable.
     */
    static navigateToChat(chatUsersInfo: ChatUsersInfoEntity): void {
        Navigator.getNavigate()(`/chat/${chatUsersInfo.peerId}`, {
            state: chatUsersInfo,
        });
    }

    /**
     * Navigate to the chat-media viewer at `/chat/media/:id` (`:id` = the chat
     * message `documentId`).
     *
     * The chat message and the peer's public JWK are passed ONLY via
     * `location.state` — `MediaDetailsPage` reads them as route args and decrypts
     * client-side. A pasted / shared URL has no args, so the page surfaces an
     * error and NEVER fetches the message by id (spec point 7 / consideration B
     * — the deliberate anti-leak behaviour). The unique `documentId` in the URL
     * keeps the per-instance route cache working.
     *
     * Call-sites (the `ImageBubble` / `VideoBubble` tap handlers, gated on the
     * message not being processing / failed) land in Task 15.
     *
     * @param documentId - The chat message `documentId` (the URL `:id`).
     * @param args - The chat message + the peer's ECDH public JWK (route args).
     */
    static navigateToMediaDetails(
        documentId: string,
        args: { message: ChatMessageEntity; peerPublicJwk: JsonWebKey | null },
    ): void {
        Navigator.getNavigate()(`/chat/media/${documentId}`, { state: args });
    }

    /**
     * Navigate to the community (per-creator wall) page at `/community/:userId`.
     *
     * Mirrors Flutter's `NavigationService.navigateToCommunityScreen(User user)`
     * (lib/services/navigation_service.dart:285), which constructs
     * `CommunityScreen(user: user)` from the whole `User` object. On web the
     * full `UserEntity` is forwarded via `location.state` so the destination
     * page can read it without a second Firestore round-trip on in-app
     * navigation (same entity-via-state pattern as `navigateToChat`). The
     * `:userId` in the URL keeps the route refresh-survivable — on deep-link /
     * refresh `CommunityPage` rehydrates the user from the URL param via
     * `getUserUseCase` (Task 13).
     *
     * @param user - The community owner's full `UserEntity`.
     */
    static navigateToCommunity(user: UserEntity): void {
        Navigator.getNavigate()(`/community/${user.firebaseId}`, {
            state: user,
        });
    }

    /**
     * Navigate to the community-media (video) player at `/community/media/:id`
     * (`:id` = the community message document id).
     *
     * Web-first surface (no Flutter parity — Flutter's community media tap pushes
     * a content-list pager). The community video pipeline mirrors chat's
     * encrypted-HLS flow, so playback opens this dedicated page which initializes
     * via `InitializeCommunityVideoUseCase` and plays through the shared
     * `InlineVideoPlayer`.
     *
     * The community message is passed ONLY via `location.state` —
     * `CommunityMediaPage` reads it as a route arg and initializes client-side.
     * A pasted / shared URL has no args, so the page surfaces an error and NEVER
     * fetches the message by id (the same deliberate anti-deep-link behaviour as
     * `navigateToMediaDetails`). The unique `messageId` in the URL keeps the
     * per-instance route cache working.
     *
     * @param messageId - The community message document id (the URL `:id`).
     * @param args - The community message (route arg).
     */
    static navigateToCommunityMedia(
        messageId: string,
        args: { message: CommunityChatMessageEntity },
    ): void {
        Navigator.getNavigate()(`/community/media/${messageId}`, { state: args });
    }

    /**
     * Navigate to the 1:1 chat video-call screen at `/video-call/:id` (`:id` =
     * the call session `callMessageId`).
     *
     * Web port of Flutter `NavigationService.navigateToVideoCallScreen`
     * (`lib/services/navigation_service.dart:966-1008`). Flutter passes five
     * Firestore side-effect callbacks (`onChannelOpened` etc.) as function refs
     * through the constructor; on web those are NOT navigated — `useVideoCall`
     * (Task 10) wires them from the Task-8 use cases — so only the data payload
     * travels. The full call context (`callerUser` / `audienceUser` /
     * `groupChatId` / `callMessageId` / `callerUserStreamingId` / `chatUsersInfo`)
     * rides `location.state` (the established web pattern — see `navigateToChat`,
     * `navigateToMediaDetails`). `VideoCallPage` reads it as route args; a
     * pasted / deep-link URL has no state, so the page surfaces an error and
     * NEVER fetches by id (anti-deep-link, consistent with `MediaDetailsPage`).
     *
     * The unique `callMessageId` in the URL keeps the per-instance route cache
     * working (same pattern as `navigateToMediaDetails`).
     *
     * Budget-exceeded navigation gate (Flutter `navigation_service.dart:982-987`):
     * when the ops budget is exceeded, Flutter shows a "budget exceeded" overlay
     * and returns WITHOUT navigating — a cost-control gate, since starting a call
     * incurs streaming / CDN cost. The web mirrors both halves: it reads the live
     * `remoteConfigService.budgetExceeded` flag (the same flag that gates video
     * init/play) and dispatches `showBudgetExceededOverlay`, the web port of
     * `AppOverlayNotifier.setShouldDisplayBudgetExceededOverlay()`, which
     * `AppOverlayHost` renders as the full-screen blocking overlay.
     *
     * `Navigator` is a static class, not a React component, so it cannot use
     * `useDispatch` — it dispatches on the `store` singleton directly (the same
     * non-component escape hatch `main.tsx` uses for `store.getState()`).
     *
     * @param args - The full call context, forwarded verbatim via
     *   `location.state` per the `VideoCallRouteArgs` contract
     *   (`presentation/feature/videoCall/args/videoCallRouteArgs.ts`). The shape is re-declared
     *   inline here, intentionally, to avoid a circular import with the videoCall
     *   feature — keep the two in sync.
     */
    static navigateToVideoCall(args: {
        callerUser: UserEntity;
        audienceUser: UserEntity;
        groupChatId: string;
        callMessageId: string;
        callerUserStreamingId?: number;
        chatUsersInfo: ChatUsersInfoEntity;
    }): void {
        // Flutter navigation_service.dart:982-987
        if (remoteConfigService.budgetExceeded) {
            store.dispatch(showBudgetExceededOverlay());
            return;
        }
        Navigator.getNavigate()(`/video-call/${args.callMessageId}`, {
            state: args,
        });
    }

    /**
     * Navigate to the community live-session screen at `/live/:id` (`:id` = the
     * live message `liveStreamingMessageId`).
     *
     * Web port of Flutter `NavigationService.navigateToLiveStreamingScreen`
     * (`lib/services/navigation_service.dart:888-922`). Flutter passes two
     * Firestore side-effect callbacks (`onBroadcasterOpenedChannel` /
     * `onBroadcasterClosedChannel`) as function refs through the constructor; on
     * web those are NOT navigated — `useLiveStreaming` (Task 10) wires them from
     * the Task-6/7 use cases — so only the data payload travels. The full live
     * context (`broadcasterFirebaseId` / `broadcasterName` /
     * `broadcasterStreamingId` / `liveStreamingMessageId`) rides `location.state`
     * (the established web pattern — see `navigateToVideoCall`, `navigateToChat`).
     * `LiveStreamingPage` reads it as route args; a pasted / deep-link URL has no
     * state, so the page surfaces an error and NEVER fetches by id (anti-deep-link,
     * consistent with `VideoCallPage` / `MediaDetailsPage`).
     *
     * The unique `liveStreamingMessageId` in the URL keeps the per-instance route
     * cache working (same pattern as `navigateToVideoCall`).
     *
     * Budget-exceeded navigation gate (Flutter `navigation_service.dart:900-905`):
     * when the ops budget is exceeded, Flutter shows a "budget exceeded" overlay
     * and returns WITHOUT navigating — a cost-control gate, since starting / joining
     * a live incurs streaming / CDN cost. The web mirrors both halves exactly as
     * `navigateToVideoCall` does: it short-circuits on the live
     * `remoteConfigService.budgetExceeded` flag and dispatches
     * `showBudgetExceededOverlay` on the `store` singleton so `AppOverlayHost`
     * raises the full-screen blocking overlay.
     *
     * @param args - The full live context, forwarded verbatim via `location.state`
     *   per the `LiveStreamingRouteArgs` contract
     *   (`presentation/feature/liveStreaming/args/liveStreamingRouteArgs.ts`). The
     *   shape is re-declared inline here, intentionally, to avoid a circular import
     *   with the liveStreaming feature — keep the two in sync.
     */
    static navigateToLiveStreaming(args: {
        broadcasterFirebaseId: string;
        broadcasterName: string;
        broadcasterStreamingId?: number;
        liveStreamingMessageId: string;
    }): void {
        // Flutter navigation_service.dart:900-905
        if (remoteConfigService.budgetExceeded) {
            store.dispatch(showBudgetExceededOverlay());
            return;
        }
        Navigator.getNavigate()(`/live/${args.liveStreamingMessageId}`, {
            state: args,
        });
    }

    /**
     * Navigate to wallet page
     * Screen key is automatically generated by RouteStack
     *
     * Mirrors Flutter's `NavigationService.navigateToWalletScreen({ bool isFromAdmin = false })`
     * (lib/services/navigation_service.dart:865). The admin "Transactions" row
     * (admin_screen.dart:79-83) opens the wallet in its admin variant by calling
     * `navigateToWalletScreen(isFromAdmin: true)`. Flutter passes the flag as a
     * `WalletScreen` constructor arg; on web the route stays `/wallet` and the flag
     * travels through React Router `location.state` (the established web pattern —
     * see `navigateToChat`), so `WalletPage` can read `{ isFromAdmin }` from state.
     *
     * @param options - Optional navigation options. `isFromAdmin` (default `false`)
     *   opens the admin-scoped wallet variant; defaulted callers land on the
     *   regular non-admin wallet.
     */
    static navigateToWallet(options?: { isFromAdmin?: boolean }): void {
        Navigator.getNavigate()('/wallet', {
            state: { isFromAdmin: options?.isFromAdmin ?? false },
        });
    }

    /**
     * Navigate to unlocked videos page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToUnlockedVideos(): void {
        Navigator.getNavigate()('/unlocked');
    }

    /**
     * Navigate to liked videos page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToLikedVideos(): void {
        Navigator.getNavigate()('/liked');
    }

    /**
     * Navigate to watch later (saved content) page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToWatchLater(): void {
        Navigator.getNavigate()('/watch-later');
    }

    /**
     * Navigate to the Roadmap ("Ideas") page at `/ideas`.
     *
     * Reached two ways, mirroring Flutter: from the top-bar menu (no argument)
     * and from a roadmap notification deep link. In the deep-link case the
     * whole `RoadmapItemInfoEntity` is passed via `location.state` so
     * `RoadmapPage` fetches the single referenced item instead of the
     * paginated list (Flutter's `widget.itemInfo != null` branch). The menu
     * entry passes nothing.
     *
     * The entity's `openLikes` / `openComments` / `likedType` flags (Flutter's
     * `RoadmapScreen.openLikes`/`openComments`/`likedType` widget params, set at
     * the push-navigation layer per the notification type) ride along inside the
     * same `location.state` payload — the caller building the entity sets them,
     * and `RoadmapPage` reads them to drive the deep-link auto-open
     * (roadmap_screen.dart:457-471).
     *
     * `options.roadmapCommentInfo` is the second half of the notification
     * payload: every roadmap case in Flutter's push navigation passes BOTH
     * `itemInfo` and `commentInfo`
     * (push_navigation_service.dart:243-300). `commentInfo` is screen-scoped —
     * `RoadmapScreen.commentInfo` (roadmap_screen.dart:35) is forwarded into
     * the single `RoadmapCommentsBottomSheet` render site
     * (roadmap_screen.dart:664-672, `commentInfo: widget.commentInfo` at :668),
     * which highlights/scrolls to the referenced comment no matter which path
     * opened the sheet.
     *
     * Flutter also declares an `openDonations` flag (roadmap_screen.dart:38/:47,
     * passed from push_navigation_service.dart:296) but never reads it, so a
     * `roadmapDonation` tap just deep-links to the board at that item with no
     * sheet — the web mirrors that by passing no flag at all.
     *
     * Screen key is automatically generated by RouteStack.
     */
    static navigateToRoadmap(
        roadmapItemInfo?: RoadmapItemInfoEntity,
        options?: { roadmapCommentInfo?: RoadmapCommentInfoEntity },
    ): void {
        const roadmapCommentInfo = options?.roadmapCommentInfo;
        // Both absent → no `state` at all, so the menu entries keep their
        // current paginated-board behaviour.
        const hasState = roadmapItemInfo != null || roadmapCommentInfo != null;
        Navigator.getNavigate()(
            '/ideas',
            hasState ? { state: { roadmapItemInfo, roadmapCommentInfo } } : undefined,
        );
    }

    /**
     * Navigate to the Add-idea page at `/ideas/add`.
     *
     * Mirrors Flutter's push of `AddRoadmapItemScreen` from the Roadmap
     * screen's "Add idea" affordance. Screen key is automatically generated by
     * RouteStack.
     */
    static navigateToAddRoadmap(): void {
        Navigator.getNavigate()('/ideas/add');
    }

    /**
     * Navigate to subscriptions page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToSubscriptions(): void {
        Navigator.getNavigate()('/subscriptions');
    }

    /**
     * Navigate to subscribers page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToSubscribers(): void {
        Navigator.getNavigate()('/subscribers');
    }

    /**
     * Navigate to saved users (Favourite people) page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToSavedUsers(): void {
        Navigator.getNavigate()('/saved-users');
    }

    /**
     * Navigate to search page
     */
    static navigateToSearch(): void {
        Navigator.getNavigate()('/search');
    }

    /**
     * Navigate to content tags page
     * Screen key is automatically generated by RouteStack
     * @param tagName - The content tag or hashtag name
     */
    static navigateToContentTag(tagName: string): void {
        Navigator.getNavigate()(`/hashtag/${encodeURIComponent(tagName)}`);
    }

    /**
     * Navigate to content label page (same as content tags but different route for clarity)
     * Screen key is automatically generated by RouteStack
     * @param tagName - The content tag or label name
     */
    static navigateToContentLabel(tagName: string): void {
        Navigator.getNavigate()(`/label/${encodeURIComponent(tagName)}`);
    }

    /**
     * Navigate to edit user profile page
     * Screen key is automatically generated by RouteStack
     */
    static navigateToEditUserProfile(): void {
        Navigator.getNavigate()('/profile/edit');
    }

    /**
     * Navigate to feed/home page
     * Screen key is automatically generated by RouteStack
     * Not used at the moment since Feed is main route
     */
    static navigateToFeed(): void {
        Navigator.getNavigate()('/');
    }

    /**
     * Navigate to inbox page
     */
    static navigateToInbox(): void {
        Navigator.getNavigate()('/inbox');
    }

    /**
     * Navigate to login page
     * Public route - no screenKey needed
     */
    static navigateToLogin(): void {
        Navigator.getNavigate()('/login');
    }

    /**
     * Replace current route (doesn't add to history)
     * @param path - The path to navigate to
     * @param state - Optional state object
     */
    static replace(path: string, state?: any): void {
        Navigator.getNavigate()(path, { replace: true, state });
    }

}

/**
 * Hook to initialize Navigator with the current navigate function
 * Use this in your root component or layout
 * 
 * @example
 * function AppLayout() {
 *   useNavigatorSetup();
 *   return <Outlet />;
 * }
 */
export const useNavigatorSetup = () => {
    // This will be imported from react-router-dom when used
    // Keeping it flexible for now
};
